"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, ShieldCheck, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Phone, CircleQuestionMark as HelpCircle, PackageCheck, Droplets, RotateCcw, Sparkles, Layers, Search } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
const productSeries = [{
  id: "dst",
  name: "DIAMOND Seal Technology (DST)",
  type: "Kitchen & Bathroom Faucets",
  cartridge: "RP50587 / RP73000",
  description: "Features a diamond-embedded ceramic disc that prevents wear, reduces leak points, and lasts up to 5 million uses.",
  typicalModels: "Leland, Essa, Trinsic, Cassidy",
  supportLife: "Lifetime Limited"
}, {
  id: "touch",
  name: "Touch2O & VoiceIQ Series",
  type: "Electronic Smart Faucets",
  cartridge: "Solenoid Valve Box / RP50587",
  description: "Capacitive touch and voice activation mechanisms powered by AA/C batteries or an optional A/C adapter.",
  typicalModels: "Trinsic Touch2O, Addison Touch, Pivotal",
  supportLife: "5-Year Electronic / Lifetime Finish"
}, {
  id: "classic",
  name: "Classic Ball & Spring Units",
  type: "Single-Handle Legacy Fixtures",
  cartridge: "RP212 Ball + RP4993 Seats & Springs",
  description: "Delta traditional rotating cam mechanism utilizing rubber seats, stainless steel springs, and a stainless steel ball.",
  typicalModels: "Classic 100/400 series, Foundations",
  supportLife: "Lifetime Limited"
}, {
  id: "twohandle",
  name: "Two-Handle Quarter-Turn Stem Units",
  type: "Widespread & Centerset Lavatory",
  cartridge: "RP25513 / RP24096 Stem Units",
  description: "Brass and ceramic 1/4-turn stems with quarter-turn stops for separate hot and cold handles.",
  typicalModels: "Windemere, Lahara, Victorian, Stryke",
  supportLife: "Lifetime Limited"
}];
const troubleshootingSteps = [{
  id: "item-1",
  issue: "Water Leaking from Faucet Spout (Constant Drip)",
  cause: "Worn cartridge seals, damaged DST ceramic disc, or degraded seats and springs.",
  procedure: "Shut off angle stops under sink. Remove handle set screw with 1/8 Allen wrench. Unscrew bonnet nut by hand or with strap wrench. Pull old cartridge (RP50587 or RP25513). Clean mineral debris with vinegar. Seat new OEM cartridge and torque bonnet nut to 10-12 ft-lbs.",
  partNeeded: "Cartridge RP50587 (Single Handle) or RP4993 (Seats & Springs)"
}, {
  id: "item-2",
  issue: "Low Water Pressure / Irregular Spray Pattern",
  cause: "Mineral buildup inside the aerator or clogged check valves in pull-down spray wand.",
  procedure: "Unscrew aerator housing using the Delta blue aerator key (RP50815) or standard pliers with cloth protection. Soak aerator assembly in warm white vinegar for 30 minutes. Flush wand supply hose with aerator removed to clear line debris. Reinstall with rubber gasket firmly seated.",
  partNeeded: "Aerator Key RP50815 / Replacement Aerator RP50815-1.5"
}, {
  id: "item-3",
  issue: "Touch2O Faucet Not Responding or Randomly Shutting Off",
  cause: "Depleted battery pack, grounded sensor wire touching brass supply line, or solenoid valve clog.",
  procedure: "Check LED base indicator color (Red flash = replace 6 AA batteries). Inspect under-sink blue sensor wire; ensure it hangs freely without touching metal plumbing lines or drain pipes. Manually bypass solenoid by turning manual bypass lever on valve box if emergency water is needed.",
  partNeeded: "Solenoid Assembly EP74857 / Delta A/C Adapter EP73954"
}, {
  id: "item-4",
  issue: "Leak from Underneath the Sink / Retractable Hose",
  cause: "Quick-Connect clip disconnected, loose hose weight, or split nylon-braided spray hose.",
  procedure: "Inspect the quick-connect block joining the pull-down hose to the faucet outlet tube. Ensure the white or gray retaining clip is clicked in place. Inspect hose for abrasion tears. If leaking through braided sheath, replace hose assembly.",
  partNeeded: "Pull-Out Hose RP50390 or RP62057 Quick-Connect Clip"
}];
const partsInventory = [{
  partNumber: "RP50587",
  category: "Cartridge",
  application: "Single-Handle DST Kitchen & Lavatory",
  stockStatus: "In Stock - Jonesboro Local Truck"
}, {
  partNumber: "RP4993",
  category: "Rebuild Kit",
  application: "Classic Ball Faucet Seats, Springs & Cams",
  stockStatus: "In Stock - Jonesboro Local Truck"
}, {
  partNumber: "RP25513",
  category: "Stem Unit",
  application: "Two-Handle Kitchen & Bath Faucets",
  stockStatus: "In Stock - Immediate Dispatch"
}, {
  partNumber: "EP74857",
  category: "Electronic",
  application: "Touch2O Generation 3 Solenoid Box",
  stockStatus: "In Stock - Jonesboro Local Truck"
}, {
  partNumber: "RP50390",
  category: "Spray Hose",
  application: "Pull-Down Kitchen Hose with Quick-Connect",
  stockStatus: "In Stock - Same Day Replacement"
}];
export default function DeltaFixtures() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="deltafixturesguide" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        ease: "easeOut"
      }} className="max-w-3xl mb-12">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="bg-secondary text-secondary-foreground border-border px-3 py-1 font-medium">
              Delta Certified Diagnostics & Repair
            </Badge>
            <span className="text-xs text-muted-foreground">Updated for 2025 Product Lines</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Delta Faucet & Sink Fixtures Guide
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Troubleshooting procedures, OEM cartridge specifications, DST rebuild steps, and warranty claim instructions for Delta kitchen and bathroom fixtures in Jonesboro, GA.
          </p>
        </motion.div>

        {/* Emergency Fast Action Banner */}
        <div className="mb-12">
          <Alert className="border-primary/20 bg-primary/5 text-foreground shadow-xs">
            <Droplets className="size-5 text-primary" />
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full">
              <div>
                <AlertTitle className="text-base font-semibold text-foreground">
                  Active Under-Sink Leak or Stuck Cartridge?
                </AlertTitle>
                <AlertDescription className="text-muted-foreground text-sm mt-0.5">
                  Our emergency vans carry original Delta OEM cartridges, seats, springs, and Touch2O solenoid units for same-day repair across Clayton County.
                </AlertDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button asChild variant="default" size="sm" className="shadow-xs font-medium">
                  <a href="tel:4708479076" className="flex items-center gap-1.5">
                    <Phone className="size-3.5" />
                    <span>Call Plumber</span>
                  </a>
                </Button>
                <Button asChild variant="outline" size="sm" className="font-medium">
                  <Link to="/faucet-sink-repair/">Repair Service</Link>
                </Button>
              </div>
            </div>
          </Alert>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="models" className="w-full space-y-8">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 h-auto p-1.5 bg-muted rounded-xl w-full max-w-3xl">
            <TabsTrigger value="models" className="py-2.5 text-xs sm:text-sm font-medium">
              <Layers className="size-4 mr-1.5 hidden sm:inline-block" />
              Product Lines
            </TabsTrigger>
            <TabsTrigger value="troubleshoot" className="py-2.5 text-xs sm:text-sm font-medium">
              <Wrench className="size-4 mr-1.5 hidden sm:inline-block" />
              Troubleshooting
            </TabsTrigger>
            <TabsTrigger value="parts" className="py-2.5 text-xs sm:text-sm font-medium">
              <PackageCheck className="size-4 mr-1.5 hidden sm:inline-block" />
              OEM Parts Stock
            </TabsTrigger>
            <TabsTrigger value="warranty" className="py-2.5 text-xs sm:text-sm font-medium">
              <ShieldCheck className="size-4 mr-1.5 hidden sm:inline-block" />
              Warranty & Service
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: Product Lines */}
          <TabsContent value="models" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {productSeries.map((item, i) => <div data-index={i} key={item.id}>
                  <Card className="h-full border border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start gap-2">
                        <CardTitle className="text-lg font-bold text-foreground">
                          {item.name}
                        </CardTitle>
                        <Badge variant="secondary" className="text-xs font-normal shrink-0">
                          {item.supportLife}
                        </Badge>
                      </div>
                      <CardDescription className="text-xs font-medium text-primary">
                        {item.type}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <p className="text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                      <Separator />
                      <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                        <div>
                          <span className="text-muted-foreground block font-medium">Core Cartridge:</span>
                          <span className="font-mono font-semibold text-foreground">{item.cartridge}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block font-medium">Common Models:</span>
                          <span className="text-foreground">{item.typicalModels}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>)}
            </div>
          </TabsContent>

          {/* TAB 2: Troubleshooting & Repair */}
          <TabsContent value="troubleshoot" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-card p-6 rounded-xl border border-border">
                  <h3 className="text-lg font-bold text-foreground mb-1 flex items-center gap-2">
                    <Wrench className="size-5 text-primary" />
                    Step-by-Step Delta Repair Solutions
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Select a symptom below to view diagnosis, teardown instructions, and required tool specifications.
                  </p>

                  <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
                    {troubleshootingSteps.map((step, i) => <div data-index={i} key={step.id}>
                        <AccordionItem value={step.id} className="border-border">
                          <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline hover:text-primary py-4">
                            <span className="flex items-start gap-2.5 pr-2">
                              <AlertTriangle className="size-4 text-accent shrink-0 mt-0.5" />
                              <span>{step.issue}</span>
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="text-muted-foreground space-y-3 pt-1 pb-4">
                            <div className="bg-muted/50 p-3 rounded-md text-xs">
                              <span className="font-bold text-foreground">Probable Cause: </span>
                              {step.cause}
                            </div>
                            <div>
                              <span className="text-xs font-bold text-foreground block mb-1">Repair Procedure:</span>
                              <p className="text-xs sm:text-sm leading-relaxed">{step.procedure}</p>
                            </div>
                            <div className="flex items-center gap-2 pt-2 text-xs">
                              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5">
                                Replacement Part: {step.partNeeded}
                              </Badge>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </div>)}
                  </Accordion>
                </div>
              </div>

              {/* Sidebar Help Card */}
              <div className="space-y-6">
                <Card className="border border-border shadow-xs bg-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-bold flex items-center gap-2">
                      <Sparkles className="size-4 text-primary" />
                      Delta Bonnet Nut Warning
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-muted-foreground space-y-2.5 leading-relaxed">
                    <p>
                      Delta DST and classic faucets use a threaded brass bonnet ring. Mineral scale can seize the ring.
                    </p>
                    <p className="font-semibold text-foreground">
                      Never force the bonnet nut with serrated pipe wrenches. This can twist and deform the underlying copper supply tubes inside the faucet body, requiring complete faucet replacement.
                    </p>
                    <p>
                      Soak seized threads with a 50/50 warm vinegar soak or use an approved strap wrench.
                    </p>
                  </CardContent>
                  <CardFooter className="pt-2">
                    <Button asChild variant="outline" size="sm" className="w-full text-xs font-medium">
                      <Link to="/contact/">Request Professional Cartridge Extraction</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <div className="p-4 rounded-xl border border-border bg-secondary/30 text-xs text-muted-foreground space-y-2">
                  <span className="font-semibold text-foreground flex items-center gap-1.5">
                    <CheckCircle2 className="size-4 text-chart-4" />
                    Tools Recommended:
                  </span>
                  <ul className="list-disc list-inside space-y-1">
                    <li>1/8" and 3/32" Hex Allen wrenches</li>
                    <li>Smooth-jaw adjustable pliers</li>
                    <li>Delta RP50815 Aerator key</li>
                    <li>Silicone plumber’s grease (food grade)</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* TAB 3: Parts Inventory */}
          <TabsContent value="parts" className="focus-visible:outline-none">
            <Card className="border border-border shadow-xs">
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div>
                    <CardTitle className="text-xl font-bold">Delta OEM Parts Stocked Locally</CardTitle>
                    <CardDescription className="text-sm">
                      Genuine Delta replacement cartridges, seals, and spray components available for Jonesboro emergency service.
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="bg-chart-4/10 text-chart-4 border-chart-4/20 font-medium">
                    Verified OEM Stock
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0 sm:px-6 sm:pb-6">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="font-semibold text-foreground">Part #</TableHead>
                      <TableHead className="font-semibold text-foreground">Category</TableHead>
                      <TableHead className="font-semibold text-foreground">Application</TableHead>
                      <TableHead className="font-semibold text-foreground">Truck Availability</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partsInventory.map((item, i) => <TableRow data-index={i} key={item.partNumber} className="border-border">
                        <TableCell className="font-mono font-bold text-primary">{item.partNumber}</TableCell>
                        <TableCell className="font-medium">{item.category}</TableCell>
                        <TableCell className="text-muted-foreground">{item.application}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 text-xs text-chart-4 font-medium">
                            <span className="size-2 rounded-full bg-chart-4 inline-block" />
                            {item.stockStatus}
                          </span>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
              <CardFooter className="flex flex-col sm:flex-row justify-between items-center gap-4 border-t border-border bg-muted/20 py-4">
                <p className="text-xs text-muted-foreground text-center sm:text-left">
                  Need a legacy part not listed? We can identify discontinued Delta fixture models via photo.
                </p>
                <Button asChild variant="default" size="sm" className="font-medium shrink-0">
                  <Link to="/contact/">Inquire About Part</Link>
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          {/* TAB 4: Warranty & Service */}
          <TabsContent value="warranty" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border border-border shadow-xs">
                <CardHeader className="pb-3">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-2">
                    <ShieldCheck className="size-5" />
                  </div>
                  <CardTitle className="text-base font-bold">Delta Lifetime Warranty</CardTitle>
                  <CardDescription className="text-xs">Original Homeowner Coverage</CardDescription>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground leading-relaxed space-y-2">
                  <p>
                    Delta Faucet Company warrants to the original consumer purchaser that parts and finishes will be free from defects in material and workmanship for as long as you own your home.
                  </p>
                  <p>
                    Commercial installations carry a 5-year parts warranty.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-border shadow-xs">
                <CardHeader className="pb-3">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-2">
                    <Search className="size-5" />
                  </div>
                  <CardTitle className="text-base font-bold">Finding Your Model Number</CardTitle>
                  <CardDescription className="text-xs">Identification Steps</CardDescription>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground leading-relaxed space-y-2">
                  <p>
                    Check for the model number tag attached to the cold water supply line under the sink (often a white or yellow band).
                  </p>
                  <p>
                    If missing, we match the escutcheon plate styling, handle type, and spout shape to the Delta technical database.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-border shadow-xs">
                <CardHeader className="pb-3">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-2">
                    <RotateCcw className="size-5" />
                  </div>
                  <CardTitle className="text-base font-bold">Professional Replacement</CardTitle>
                  <CardDescription className="text-xs">Same-Day Installation</CardDescription>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground leading-relaxed space-y-2">
                  <p>
                    If the fixture body is cracked, corroded beyond repair, or has stripped threads, our plumbing technicians can install a brand new Delta kitchen or bathroom unit immediately.
                  </p>
                  <p>
                    All labor backed by our local workmanship guarantee.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 p-6 rounded-2xl bg-primary text-primary-foreground flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="space-y-1 text-center sm:text-left">
                <h3 className="text-lg font-bold">Need Immediate Help With a Delta Faucet?</h3>
                <p className="text-sm text-primary-foreground/80 max-w-xl">
                  Speak directly with an experienced Jonesboro plumber for immediate troubleshooting advice or to dispatch a technician with genuine Delta replacement parts.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
                <Button asChild variant="secondary" size="lg" className="font-semibold shadow-sm">
                  <a href="tel:4708479076" className="flex items-center gap-2">
                    <Phone className="size-4" />
                    <span>(470) 847-9076</span>
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/20 font-medium">
                  <Link to="/faucet-sink-repair/">View Faucet Services</Link>
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>;
}